import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, Lock, User, Building, Users, Package } from "lucide-react";
import { signInSchema, signUpSchema, getFieldErrors, type FieldErrors } from "@/lib/validations";
import { FormError } from "@/components/ui/form-error";

export default function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState<"brand" | "designer" | "">("");
  const [loading, setLoading] = useState(false);
  const [signInErrors, setSignInErrors] = useState<FieldErrors>({});
  const [signUpErrors, setSignUpErrors] = useState<FieldErrors>({});
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn, signUp, user } = useAuth();
  const { toast } = useToast();
  
  const defaultTab = searchParams.get("tab") || "signin";

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const validateSignInField = (field: string, value: string) => {
    const data = {
      email: field === "email" ? value : email,
      password: field === "password" ? value : password,
    };
    
    const result = signInSchema.safeParse(data);
    const fieldErrors = getFieldErrors(result);
    
    setSignInErrors(prev => ({
      ...prev,
      [field]: fieldErrors[field] || "",
    }));
  };

  const validateSignUpField = (field: string, value: unknown) => {
    const data = {
      fullName: field === "fullName" ? value : fullName,
      email: field === "email" ? value : email,
      company: field === "company" ? value : company,
      role: field === "role" ? value : role || undefined,
      password: field === "password" ? value : password,
    };
    
    const result = signUpSchema.safeParse(data);
    const fieldErrors = getFieldErrors(result);
    
    setSignUpErrors(prev => ({
      ...prev,
      [field]: fieldErrors[field] || "",
    }));
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address first.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    setLoading(false);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Check your email",
        description: "We've sent you a password reset link."
      });
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = signInSchema.safeParse({ email, password });
    
    if (!result.success) {
      setSignInErrors(getFieldErrors(result));
      return;
    }

    setSignInErrors({});
    setLoading(true);
    const { error } = await signIn(result.data.email, result.data.password);
    
    if (error) {
      toast({
        title: "Sign In Failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "Successfully signed in to HIDNstock"
      });
      navigate("/");
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = signUpSchema.safeParse({ 
      fullName, 
      email, 
      company, 
      role: role || undefined, 
      password 
    });
    
    if (!result.success) {
      setSignUpErrors(getFieldErrors(result));
      return;
    }

    setSignUpErrors({});
    setLoading(true);
    const { error } = await signUp(result.data.email, result.data.password);
    
    if (error) {
      if (error.message.includes("already registered")) {
        toast({
          title: "Account exists",
          description: "This email is already registered. Please sign in instead.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Sign Up Failed",
          description: error.message,
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "Welcome to HIDNstock!",
        description: "Please check your email to verify your account."
      });
    }
    setLoading(false);
  };

  return (
    <Layout>
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="mb-4">Join HIDNstock</h1>
            <p className="text-muted-foreground">
              Connect with fashion professionals and transform deadstock into opportunities
            </p>
          </div>

          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Get Started</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <Card>
                <CardHeader>
                  <CardTitle>Sign In</CardTitle>
                  <CardDescription>
                    Welcome back to HIDNstock
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (signInErrors.email) validateSignInField("email", e.target.value);
                          }}
                          onBlur={() => validateSignInField("email", email)}
                          className={`pl-10 ${signInErrors.email ? "border-destructive" : ""}`}
                        />
                      </div>
                      <FormError message={signInErrors.email} />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-password"
                          type="password"
                          placeholder="Your password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (signInErrors.password) validateSignInField("password", e.target.value);
                          }}
                          onBlur={() => validateSignInField("password", password)}
                          className={`pl-10 ${signInErrors.password ? "border-destructive" : ""}`}
                        />
                      </div>
                      <FormError message={signInErrors.password} />
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Sign In
                    </Button>

                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="w-full text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline"
                      disabled={loading}
                    >
                      Forgot your password?
                    </button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="signup">
              <Card>
                <CardHeader>
                  <CardTitle>Create Account</CardTitle>
                  <CardDescription>
                    Join the premium marketplace for fashion professionals
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="Your full name"
                          value={fullName}
                          onChange={(e) => {
                            setFullName(e.target.value);
                            if (signUpErrors.fullName) validateSignUpField("fullName", e.target.value);
                          }}
                          onBlur={() => validateSignUpField("fullName", fullName)}
                          className={`pl-10 ${signUpErrors.fullName ? "border-destructive" : ""}`}
                        />
                      </div>
                      <FormError message={signUpErrors.fullName} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (signUpErrors.email) validateSignUpField("email", e.target.value);
                          }}
                          onBlur={() => validateSignUpField("email", email)}
                          className={`pl-10 ${signUpErrors.email ? "border-destructive" : ""}`}
                        />
                      </div>
                      <FormError message={signUpErrors.email} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-company">Company (Optional)</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-company"
                          type="text"
                          placeholder="Your company name"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>I am a *</Label>
                      <div className="grid grid-cols-1 gap-3">
                         <div 
                          className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-soft ${
                            role === "brand" 
                              ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
                              : signUpErrors.role 
                                ? "border-destructive" 
                                : "border-border hover:border-primary/50"
                          }`}
                          onClick={() => {
                            setRole("brand");
                            validateSignUpField("role", "brand");
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-5 h-5 rounded-full border-2 mt-0.5 ${
                              role === "brand" 
                                ? "border-primary bg-primary" 
                                : "border-muted-foreground"
                            }`}>
                              {role === "brand" && (
                                <div className="w-full h-full rounded-full bg-white scale-50" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <Package className="h-4 w-4 text-primary" />
                                  <span className="font-semibold">Brand / Manufacturer</span>
                                </div>
                                <span className="text-lg font-bold text-primary">$99/mo</span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                I have deadstock materials or want to post remake projects for designers
                              </p>
                            </div>
                          </div>
                        </div>

                        <div 
                          className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-soft ${
                            role === "designer" 
                              ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
                              : signUpErrors.role 
                                ? "border-destructive" 
                                : "border-border hover:border-primary/50"
                          }`}
                          onClick={() => {
                            setRole("designer");
                            validateSignUpField("role", "designer");
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-5 h-5 rounded-full border-2 mt-0.5 ${
                              role === "designer" 
                                ? "border-primary bg-primary" 
                                : "border-muted-foreground"
                            }`}>
                              {role === "designer" && (
                                <div className="w-full h-full rounded-full bg-white scale-50" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <Users className="h-4 w-4 text-primary" />
                                  <span className="font-semibold">Designer / Upcycler</span>
                                </div>
                                <span className="text-lg font-bold text-primary">$9/mo</span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                I want to buy deadstock materials and bid on remake projects
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <FormError message={signUpErrors.role} />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type="password"
                          placeholder="Create a password (min. 6 characters)"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (signUpErrors.password) validateSignUpField("password", e.target.value);
                          }}
                          onBlur={() => validateSignUpField("password", password)}
                          className={`pl-10 ${signUpErrors.password ? "border-destructive" : ""}`}
                          minLength={6}
                        />
                      </div>
                      <FormError message={signUpErrors.password} />
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Create Account
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      By creating an account, you agree to our Terms of Service and Privacy Policy
                    </p>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
